import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const pf = async (endpoint, options = {}) => {
  const res = await fetch(`${API_URL}/api/platform${endpoint}`, {
    ...options, credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Request failed');
  }
  return res.json();
};

const PlatformDiscounts = () => {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ code: '', type: 'percentage', value: '', max_uses: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await pf('/discounts');
      setDiscounts(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.code.trim()) return;
    try {
      await pf('/discounts', {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          value: parseFloat(form.value) || 0,
          max_uses: parseInt(form.max_uses) || 0,
        }),
      });
      toast.success('Discount created');
      setShowCreate(false);
      setForm({ code: '', type: 'percentage', value: '', max_uses: '' });
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeactivate = async (discountId) => {
    try {
      await pf(`/discounts/${discountId}/deactivate`, { method: 'POST' });
      toast.success('Discount deactivated');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="space-y-4" data-testid="platform-discounts">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-100">Discounts</h1>
          <p className="text-xs text-slate-500">{discounts.length} discount codes</p>
        </div>
        <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-500" onClick={() => setShowCreate(true)} data-testid="platform-create-discount">
          <Plus className="w-3.5 h-3.5 mr-1" /> Create
        </Button>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-[10px] text-slate-500 font-medium">Code</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Type</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Value</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Usage</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Status</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Expires</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {discounts.map(d => (
                <TableRow key={d.discount_id} className="border-slate-800/50 hover:bg-slate-800/30" data-testid={`discount-row-${d.discount_id}`}>
                  <TableCell>
                    <code className="text-xs font-mono text-slate-200">{d.code}</code>
                  </TableCell>
                  <TableCell className="text-xs text-slate-400 capitalize">{d.type.replace('_', ' ')}</TableCell>
                  <TableCell className="text-xs text-slate-400">
                    {d.type === 'percentage' ? `${d.value}%` : d.type === 'flat' ? `Rs.${d.value}` : 'Free'}
                  </TableCell>
                  <TableCell className="text-xs text-slate-400">
                    {d.used_count}{d.max_uses ? `/${d.max_uses}` : '/Unlimited'}
                  </TableCell>
                  <TableCell>
                    <Badge className={`text-[10px] ${d.is_active ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-slate-700/30 text-slate-500 border-slate-600'}`}>
                      {d.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-[10px] text-slate-500">
                    {d.valid_until ? new Date(d.valid_until).toLocaleDateString() : 'Never'}
                  </TableCell>
                  <TableCell>
                    {d.is_active && (
                      <button
                        className="text-[10px] px-2 py-0.5 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                        onClick={() => handleDeactivate(d.discount_id)}
                        data-testid={`deactivate-discount-${d.discount_id}`}
                      >
                        Deactivate
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {discounts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-xs text-slate-500 py-8">No discounts yet</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-200">Create Discount Code</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Code</Label>
              <Input
                value={form.code}
                onChange={(e) => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="e.g. LAUNCH50"
                className="h-8 text-xs bg-slate-800 border-slate-700 text-slate-300"
                data-testid="platform-discount-code"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm(f => ({ ...f, type: v }))}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-300 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="percentage" className="text-slate-300 text-xs">Percentage Off</SelectItem>
                  <SelectItem value="flat" className="text-slate-300 text-xs">Flat Amount Off</SelectItem>
                  <SelectItem value="full_discount" className="text-slate-300 text-xs">100% Free</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.type !== 'full_discount' && (
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-400">{form.type === 'percentage' ? 'Percentage' : 'Amount (Rs.)'}</Label>
                <Input
                  type="number"
                  value={form.value}
                  onChange={(e) => setForm(f => ({ ...f, value: e.target.value }))}
                  className="h-8 text-xs bg-slate-800 border-slate-700 text-slate-300"
                  data-testid="platform-discount-value"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Max Uses (0 = unlimited)</Label>
              <Input
                type="number"
                value={form.max_uses}
                onChange={(e) => setForm(f => ({ ...f, max_uses: e.target.value }))}
                className="h-8 text-xs bg-slate-800 border-slate-700 text-slate-300"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="border-slate-700 text-slate-300 h-7 text-xs" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 h-7 text-xs" onClick={handleCreate} disabled={!form.code.trim()} data-testid="platform-confirm-create-discount">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformDiscounts;
